import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import PortalHeader from '../../components/Portal/PortalHeader';
import WaButton from '../../components/wa/WaButton';
import { fetchStudentsByFamily } from '../../services/studentService';
import { fetchOpenMakeupSlots, redeemMakeupCredit } from '../../services/makeupService';
import { ymd } from '../../utils/occurrenceExpansion';
import { formatTime12Hour } from '../../utils/schedulingUtils';
import { LOCATIONS } from '../EmployeePortal/Scheduling/locations';

// Makeup calendar / redeem surface (Track 2 Slice C — fall model §3f/§4).
// Lists the OPEN makeup pool — readable family-agnostically by any signed-in
// customer; the query below MUST carry both equality constraints because that
// is exactly what the read rule grants (rules are not filters). Redeem
// eligibility (credit balance, family ownership) is the callable's job: the
// server transaction is authoritative and first-caller-wins on a race.
//
// States (all deliberate):
//   - zero balance  → calendar still shown, redeem disabled (honest, not hidden)
//   - empty pool    → "No makeup times are available right now."
//   - race loss     → "That time was just booked — pick another" + refresh
//   - multi-child   → parent picks whose credit is spent (decision 4);
//                     exactly one credit-holding child auto-selects.

const DOW_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MON_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const studentDisplayName = (s) =>
	s.fullName || [s.firstName, s.lastName].filter(Boolean).join(' ') || s.name || 'Student';

const dateLabel = (dateStr) => {
	const d = new Date(dateStr + 'T00:00:00');
	return `${DOW_FULL[d.getDay()]}, ${MON_ABBR[d.getMonth()]} ${d.getDate()}`;
};

// Canonical location label off the shared LOCATIONS enum — fail-closed: a
// value outside the enum never renders as a guessed studio name.
const locationChip = (value) => {
	const match = LOCATIONS.find((l) => l.value === value);
	return match || { name: value || 'Location TBD', text: '#555', fill: '#f2f2f2', dot: '#999' };
};

const MakeupCalendar = () => {
	const { userProfile } = useAuth();
	const familyId = userProfile?.familyId || null;

	const [students, setStudents] = useState([]);
	const [openSlots, setOpenSlots] = useState([]);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState(false);

	// Redeem-confirm dialog: the slot being booked + the chosen child.
	const [confirmSlot, setConfirmSlot] = useState(null);
	const [chosenStudentId, setChosenStudentId] = useState(null);
	const [redeemBusy, setRedeemBusy] = useState(false);
	const dialogRef = useRef(null);
	useEffect(() => {
		if (dialogRef.current) dialogRef.current.open = !!confirmSlot;
	}, [confirmSlot]);
	useEffect(() => {
		const el = dialogRef.current;
		if (!el) return undefined;
		const onHide = () => setConfirmSlot(null);
		el.addEventListener('wa-after-hide', onHide);
		return () => el.removeEventListener('wa-after-hide', onHide);
	}, []);

	const [toast, setToast] = useState(null);
	const toastTimer = useRef(null);
	useEffect(() => () => clearTimeout(toastTimer.current), []);
	const showToast = (msg) => {
		setToast(msg);
		clearTimeout(toastTimer.current);
		toastTimer.current = setTimeout(() => setToast(null), 6000);
	};

	const todayStr = ymd(new Date());

	const loadSlots = useCallback(async () => {
		// Both where() constraints live in the service — the rule grants exactly
		// status:'open' && isMakeup:true. Past dates are trimmed client-side.
		const pool = await fetchOpenMakeupSlots();
		return pool
			.filter((s) => (s.specificDate || '') >= todayStr)
			.sort((a, b) => (a.specificDate === b.specificDate
				? (a.startTime || '').localeCompare(b.startTime || '')
				: (a.specificDate || '').localeCompare(b.specificDate || '')));
	}, [todayStr]);

	useEffect(() => {
		if (!familyId) {
			setLoading(false);
			return;
		}
		let cancelled = false;
		(async () => {
			try {
				const [kids, pool] = await Promise.all([fetchStudentsByFamily(familyId), loadSlots()]);
				if (cancelled) return;
				setStudents(kids);
				setOpenSlots(pool);
			} catch (err) {
				console.error('Error loading makeup calendar:', err);
				if (!cancelled) setLoadError(true);
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => { cancelled = true; };
	}, [familyId, loadSlots]);

	const creditHolders = students.filter((s) => (s.makeupBalance || 0) > 0);
	const totalCredits = students.reduce((n, s) => n + (s.makeupBalance || 0), 0);

	const openConfirm = (slot) => {
		if (totalCredits <= 0) return;
		// Decision 4: exactly one credit-holding child auto-selects; more than
		// one → the parent picks whose credit is spent (and who is slotted).
		setChosenStudentId(creditHolders.length === 1 ? creditHolders[0].id : null);
		setConfirmSlot(slot);
	};

	const performRedeem = async () => {
		if (!confirmSlot || !chosenStudentId || redeemBusy) return;
		setRedeemBusy(true);
		try {
			await redeemMakeupCredit(confirmSlot.id, chosenStudentId);
			const child = students.find((s) => s.id === chosenStudentId);
			showToast(`Makeup lesson booked${child ? ` for ${studentDisplayName(child)}` : ''}! It's on your schedule now.`);
			setOpenSlots((prev) => prev.filter((s) => s.id !== confirmSlot.id));
			setStudents((prev) => prev.map((st) => (st.id === chosenStudentId
				? { ...st, makeupBalance: Math.max(0, (st.makeupBalance || 0) - 1) }
				: st)));
			setConfirmSlot(null);
		} catch (err) {
			// First-caller-wins race: the server rejects a just-taken slot with
			// failed-precondition — refresh the pool so the stale row drops.
			if (err?.code === 'functions/failed-precondition' && /just booked/i.test(err.message || '')) {
				showToast('That time was just booked — pick another.');
				setConfirmSlot(null);
				try {
					setOpenSlots(await loadSlots());
				} catch (refreshErr) {
					console.error('Pool refresh after race loss failed:', refreshErr);
				}
			} else {
				console.error('redeemMakeupCredit failed:', err);
				showToast("We couldn't book that makeup lesson. Please try again, or contact us.");
			}
		} finally {
			setRedeemBusy(false);
		}
	};

	const confirmChild = confirmSlot && chosenStudentId
		? students.find((s) => s.id === chosenStudentId)
		: null;

	return (
		<>
			<PortalHeader
				title='Schedule a Makeup'
				subtitle='Use a makeup credit to book an open lesson time.'
			/>
			<div style={{ maxWidth: 760, margin: '0 auto', padding: '1.5rem 1rem 3rem' }}>
				<p style={{ margin: '0 0 1rem' }}>
					<Link to='/portal/customer/lessons'>
						<i className='fa-sharp fa-solid fa-arrow-left' style={{ marginRight: '0.4rem' }}></i>
						Back to My Lessons
					</Link>
				</p>

				{loading && <p style={{ textAlign: 'center', padding: '2rem 0' }}>Loading makeup times…</p>}

				{!loading && loadError && (
					<wa-card style={{ display: 'block' }}>
						<p style={{ margin: 0 }}>
							We couldn't load makeup times just now. Please refresh, or{' '}
							<a href='/contact'>contact us</a> if it keeps happening.
						</p>
					</wa-card>
				)}

				{!loading && !loadError && !familyId && (
					<wa-card style={{ display: 'block' }}>
						<p style={{ margin: 0 }}>
							Your account isn't linked to a family yet. If you think this is a
							mistake, <a href='/contact'>contact us</a> and we'll get you set up.
						</p>
					</wa-card>
				)}

				{!loading && !loadError && familyId && (
					<>
						{/* Honest zero-balance state: the calendar stays visible, booking is off. */}
						{totalCredits <= 0 && (
							<wa-card style={{ display: 'block', marginBottom: '1rem' }}>
								<p style={{ margin: 0, color: '#666' }}>
									<i className='fa-sharp fa-solid fa-circle-info' style={{ marginRight: '0.5rem' }}></i>
									You don't have a makeup credit to use yet. Credits are earned
									when a lesson is cancelled at least 24 hours ahead.
								</p>
							</wa-card>
						)}

						{openSlots.length === 0 && (
							<wa-card style={{ display: 'block' }}>
								<p style={{ margin: 0 }}>
									No makeup times are available right now. Check back soon — new
									times open up when lessons are cancelled.
								</p>
							</wa-card>
						)}

						{openSlots.length > 0 && (
							<wa-card style={{ display: 'block' }}>
								<ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
									{openSlots.map((slot, i) => {
										const lc = locationChip(slot.location);
										return (
											<li
												key={slot.id}
												style={{
													display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.35rem 0.75rem',
													padding: '0.65rem 0',
													borderTop: i === 0 ? 'none' : '1px solid var(--wa-color-surface-border, #eee)',
												}}>
												<span style={{ fontWeight: 600, minWidth: '9.5rem' }}>{dateLabel(slot.specificDate)}</span>
												<span>
													{formatTime12Hour(slot.startTime)}
													{slot.endTime ? `–${formatTime12Hour(slot.endTime)}` : ''}
												</span>
												<span style={{
													display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
													background: lc.fill, color: lc.text, borderRadius: 999,
													padding: '0.1rem 0.6rem', fontSize: '0.85rem',
												}}>
													<span style={{ width: 8, height: 8, borderRadius: '50%', background: lc.dot, display: 'inline-block' }}></span>
													{lc.name}
												</span>
												<span style={{ marginLeft: 'auto' }}>
													<WaButton
														size='small'
														variant='brand'
														disabled={totalCredits <= 0}
														onClick={() => openConfirm(slot)}>
														Use a credit
													</WaButton>
												</span>
											</li>
										);
									})}
								</ul>
							</wa-card>
						)}
					</>
				)}
			</div>

			{/* Redeem-confirm dialog (decision 4: child selector when several
			    children hold credits; auto-selected when exactly one does). */}
			<wa-dialog ref={dialogRef} label='Use a makeup credit'>
				{confirmSlot && (
					<div>
						<p style={{ marginTop: 0 }}>
							<strong>{dateLabel(confirmSlot.specificDate)}</strong>,{' '}
							{formatTime12Hour(confirmSlot.startTime)}
							{confirmSlot.endTime ? `–${formatTime12Hour(confirmSlot.endTime)}` : ''}{' '}
							at {locationChip(confirmSlot.location).name}.
						</p>
						{creditHolders.length > 1 ? (
							<fieldset style={{ border: 'none', margin: 0, padding: 0 }}>
								<legend style={{ fontSize: '0.95rem', fontWeight: 600, marginBottom: '0.4rem' }}>
									Whose makeup credit?
								</legend>
								{creditHolders.map((s) => (
									<label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', cursor: 'pointer' }}>
										<input
											type='radio'
											name='makeup-child'
											checked={chosenStudentId === s.id}
											onChange={() => setChosenStudentId(s.id)}
										/>
										<span>
											{studentDisplayName(s)}{' '}
											<span style={{ color: '#888', fontSize: '0.85rem' }}>
												({s.makeupBalance} credit{s.makeupBalance === 1 ? '' : 's'})
											</span>
										</span>
									</label>
								))}
							</fieldset>
						) : (
							confirmChild && (
								<p style={{ color: '#666' }}>
									Use {studentDisplayName(confirmChild)}'s makeup credit for this lesson?
								</p>
							)
						)}
					</div>
				)}
				<div slot='footer' style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
					<WaButton appearance='outlined' disabled={redeemBusy} onClick={() => setConfirmSlot(null)}>
						Never mind
					</WaButton>
					<WaButton
						variant='brand'
						loading={redeemBusy}
						disabled={redeemBusy || !chosenStudentId}
						onClick={performRedeem}>
						Book this makeup
					</WaButton>
				</div>
			</wa-dialog>

			{/* Result toast */}
			{toast && (
				<div style={{ position: 'fixed', left: '50%', bottom: 24, transform: 'translateX(-50%)', zIndex: 60, maxWidth: 'min(92vw, 26rem)', background: '#384971', color: '#fff', padding: '12px 18px', borderRadius: 12, boxShadow: '0 16px 34px -12px rgba(56,73,113,.6)' }}>
					<span style={{ fontSize: '13.5px', fontWeight: 500 }}>{toast}</span>
				</div>
			)}
		</>
	);
};

export default MakeupCalendar;
